import { useState } from 'react'
import classNames from 'classnames'
import './GiftNoteDialog.scss'
import * as S from './GiftNoteDialog.styles'
import { giftNoteManager } from '../../services/GiftNoteManager'
import GiftNote from './GiftNote/GiftNote'
import MixtilesDialog from '../MixtilesDialog/MixtilesDialog'
import MixtilesButtonsDrawer from '../MixtilesButtonsDrawer/MixtilesButtonsDrawer'
import BasicDialogButton from '../MixtilesDialog/BasicDialogButton/BasicDialogButton'
import useApi from '../../api/apiProvider'
import { saveOrderDraftApi } from '../../api/orderDraft.api'
import TopBar from '../../elements/TopBar/TopBar'
import BackButton from '../../elements/TopBar/Buttons/BackButton'
import Button from 'mixtiles-web-common/ui/Button'
import { logger } from '../../services/logger'
import useOnMount from 'hooks/useOnMount'
import { useUserDeviceData } from 'services/UserDeviceContext'
import { useTranslation } from 'react-i18next'

function GiftNoteContent({ onClose }) {
  const { t } = useTranslation()
  const { isMobile, isTablet } = useUserDeviceData()
  const api = useApi()

  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(() => giftNoteManager.getGiftNote())
  const [showEditOptions, setShowEditOptions] = useState(false)
  const [animatingButton, setAnimatingButton] = useState(
    () => !giftNoteManager.getGiftNote()
  )

  useOnMount(() => {
    if (animatingButton) {
      const timeout = setTimeout(() => setAnimatingButton(false), 2000)
      return () => clearTimeout(timeout)
    }
  })

  const handleNoteChange = ({ target: { value } }) => setNote(value)

  const handleStartEditingClick = () => {
    setShowEditOptions(false)
    // This is set as callback, to separate the two renders of GiftNote.
    // If we won't - the textarea will not be focused after selecting "Edit Text"
    setTimeout(() => setEditing(true), 0)
  }

  const handleBottomButtonClick = () => {
    if (note) {
      setShowEditOptions(true)
    } else {
      handleStartEditingClick()
    }
  }

  const handleCancelWritingClick = () => {
    setEditing(false)
    setNote(giftNoteManager.getGiftNote())
  }

  const saveGiftNoteToOrderDraft = async (giftNote) => {
    if (api.isAuthenticated) {
      await api
        .call(saveOrderDraftApi({ giftNote }))
        .catch((e) =>
          logger.error('Save order draft failed during git note', e)
        )
    }
  }

  const handleDoneWriting = async () => {
    giftNoteManager.setGiftNote(note)
    await saveGiftNoteToOrderDraft(giftNoteManager.getGiftNote())
    onClose()
  }

  const closeEditOptions = () => {
    setShowEditOptions(false)
  }

  const removeGiftNote = async () => {
    giftNoteManager.setGiftNote(null)
    // Without clearing the draft too, account sync restores the note on the next load
    await saveGiftNoteToOrderDraft(null)
    onClose()
  }

  const bottomButtonText = () => {
    if (note) {
      return t('order.checkout.add_gift_note_options.edit_note')
    }
    return t('order.checkout.add_gift_note_options.start_writing')
  }

  const leftButton = editing ? (
    <BackButton onClick={handleCancelWritingClick} />
  ) : (
    <S.CloseButton onClick={onClose}>
      {t('order.checkout.add_gift_note_options.close')}
    </S.CloseButton>
  )

  return (
    <div
      className={classNames('gift-note-page', {
        editing,
        mobile: isMobile || isTablet,
      })}
    >
      <TopBar
        rightButton={
          editing ? (
            <S.DoneButton
              onClick={handleDoneWriting}
              data-testid="gift-note-done-button"
            >
              {t('general.done')}
            </S.DoneButton>
          ) : null
        }
        leftButton={leftButton}
      >
        <TopBar.TitleContent>
          <></>
        </TopBar.TitleContent>
      </TopBar>

      <div className="content">
        <S.Header $editing={editing}>
          {t('order.checkout.add_gift_note_options.header')}
        </S.Header>
        <S.Description $editing={editing}>
          {t('order.checkout.add_gift_note_options.description')}
        </S.Description>
        <GiftNote
          editing={editing}
          note={note}
          onChange={handleNoteChange}
          onClick={handleStartEditingClick}
        />
      </div>

      <S.Cta $editing={editing}>
        <Button
          size={isMobile ? 'lg' : 'xl'}
          fullWidth={isMobile}
          minWidth={isMobile ? null : '364px'}
          onClick={handleBottomButtonClick}
          classes={classNames({ animating: animatingButton })}
          testId="gift-note-cta-button"
        >
          {bottomButtonText()}
        </Button>
      </S.Cta>

      <MixtilesButtonsDrawer open={showEditOptions} onClose={closeEditOptions}>
        <BasicDialogButton
          key="edit"
          action={handleStartEditingClick}
          noBorder
          testId="gift-note-edit-button"
        >
          {t('order.checkout.add_gift_note_options.edit_options.edit')}
        </BasicDialogButton>
        <BasicDialogButton
          key="remove"
          action={removeGiftNote}
          testId="gift-note-remove-button"
        >
          {t('order.checkout.add_gift_note_options.edit_options.remove')}
        </BasicDialogButton>
        <BasicDialogButton key="dismiss" action={closeEditOptions} gray>
          {t('order.checkout.add_gift_note_options.edit_options.dismiss')}
        </BasicDialogButton>
      </MixtilesButtonsDrawer>
    </div>
  )
}

function GiftNoteDialog({ open, onClose }) {
  return (
    <MixtilesDialog
      open={open}
      onDismiss={onClose}
      fullScreen
      transition="grow"
    >
      <GiftNoteContent onClose={onClose} />
    </MixtilesDialog>
  )
}

export default GiftNoteDialog
